/**
 * シフトの変更申請(2026-09-20 実装。D-113)。
 *  - operator: 提出済みのシフトを変えたいとき、日・希望・**理由**を入れて申請する。自分の申請と状態を見る
 *  - admin: 承認待ちの申請を承認/却下する。承認するとシフト提出のその日が書き換わり、変更履歴に残る
 * ※ 出勤表(その日の割振)は管理者が作るものなので、この申請では触らない。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@core/auth/AuthProvider';
import { useToast } from '@core/components/Toast';
import FilterBar from '@core/components/ui/FilterBar';
import Panel from '@core/components/ui/Panel';
import StatusBadge, { type StatusKind } from '@core/components/ui/StatusBadge';
import { IconChevronLeft, IconChevronRight } from '@core/components/icons';
import { todayStr, weekdayLabel } from '@core/lib/date';
import type { DateStr, MonthStr, Operator } from '@core/lib/types';
import { fetchOperatorById, fetchOperators } from '@core/masters/operatorRepo';
import { addMonths, daysInMonth } from '@core/dashboard/calc';
import { fetchSubmission, type ShiftSubmission } from './shiftSubmitRepo';
import {
  CHANGE_STATUS_LABELS,
  approveChangeRequest,
  cancelChangeRequest,
  createChangeRequest,
  fetchChangeRequests,
  rejectChangeRequest,
  wishLabel,
  type ChangeStatus,
  type ChangeWish,
  type ShiftChangeRequest,
} from './shiftChangeRepo';

/** 8:00〜19:00 の30分刻み(シフト提出と同じ) */
const hours = Array.from({ length: 23 }, (_, i) => 8 + i * 0.5);
const hhmm = (h: number) => `${Math.floor(h)}:${h % 1 ? '30' : '00'}`;
/** 状態 → バッジの種類(承認済=確認済 / 却下=要対応 / 承認待ち=未確認) */
const kindOf = (s: ChangeStatus): StatusKind => (s === 'approved' ? 'confirmed' : s === 'rejected' ? 'danger' : 'unconfirmed');

export default function ShiftChangeTab() {
  const { user, isAdmin } = useAuth();
  const toast = useToast();
  const [month, setMonth] = useState<MonthStr>(todayStr().slice(0, 7) as MonthStr);
  const [opId, setOpId] = useState(user?.op_id ?? '');
  const [operators, setOperators] = useState<Operator[]>([]);
  const [me, setMe] = useState<Operator | null>(null);
  const [sub, setSub] = useState<ShiftSubmission | null>(null);
  const [list, setList] = useState<ShiftChangeRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // 申請フォーム
  const [day, setDay] = useState('');
  const [rest, setRest] = useState(false);
  const [start, setStart] = useState(9);
  const [end, setEnd] = useState(18);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (isAdmin) void fetchOperators().then(setOperators).catch(() => undefined);
    else if (user?.op_id) void fetchOperatorById(user.op_id).then(setMe).catch(() => undefined);
  }, [isAdmin, user?.op_id]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rs, s] = await Promise.all([fetchChangeRequests(month, isAdmin ? undefined : (user?.op_id ?? '')), opId ? fetchSubmission(month, opId) : Promise.resolve(null)]);
      setList(rs);
      setSub(s);
    } catch (e) {
      toast(e instanceof Error ? e.message : '読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, [month, opId, isAdmin, user?.op_id, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const target = useMemo(() => (isAdmin ? (operators.find((o) => o.id === opId) ?? null) : me), [isAdmin, operators, opId, me]);
  const days = useMemo(() => Array.from({ length: daysInMonth(month) }, (_, i) => String(i + 1).padStart(2, '0')), [month]);
  const current: ChangeWish = day && sub?.days[day] ? { start: sub.days[day]!.start, end: sub.days[day]!.end } : null;
  const pending = list.filter((r) => r.status === 'pending');

  const submit = async () => {
    if (!user || !opId || !day) return;
    setBusy(true);
    try {
      await createChangeRequest({
        month,
        day,
        opId,
        name: target?.name ?? user.name ?? '',
        bbId: sub?.bb_id ?? target?.bb_accounts[0]?.bb_id ?? '',
        after: rest ? null : { start, end },
        reason,
        userId: user.id,
      });
      setReason('');
      setDay('');
      toast('変更を申請しました。管理者の承認をお待ちください');
      await load();
    } catch (e) {
      toast(e instanceof Error ? e.message : '申請に失敗しました');
    } finally {
      setBusy(false);
    }
  };

  const decide = async (r: ShiftChangeRequest, ok: boolean) => {
    if (!user) return;
    const who = { id: user.id, name: user.name ?? '' };
    const note = ok ? '' : (window.prompt(`${r.name} さんの ${Number(r.day)}日の申請を却下します。理由を入力してください`) ?? '');
    if (!ok && !note.trim()) return;
    setBusy(true);
    try {
      if (ok) await approveChangeRequest(r.id, who);
      else await rejectChangeRequest(r.id, who, note);
      toast(ok ? '承認しました(シフト提出に反映しました)' : '却下しました');
      await load();
    } catch (e) {
      toast(e instanceof Error ? e.message : '処理に失敗しました');
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (r: ShiftChangeRequest) => {
    if (!user) return;
    setBusy(true);
    try {
      await cancelChangeRequest(r.id, user.id);
      toast('申請を取り下げました');
      await load();
    } catch (e) {
      toast(e instanceof Error ? e.message : '取り下げに失敗しました');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <FilterBar label="対象の月">
        <span className="ui-date-nav">
          <button type="button" className="ui-btn ui-btn-secondary ui-btn-icon" onClick={() => setMonth(addMonths(month, -1) as MonthStr)} aria-label="前の月">
            <IconChevronLeft size={16} />
          </button>
          <input type="month" className="ui-input" aria-label="月" value={month} onChange={(e) => e.target.value && setMonth(e.target.value as MonthStr)} />
          <button type="button" className="ui-btn ui-btn-secondary ui-btn-icon" onClick={() => setMonth(addMonths(month, 1) as MonthStr)} aria-label="次の月">
            <IconChevronRight size={16} />
          </button>
        </span>
        <span className="ui-period-label">
          {month.slice(0, 4)}年{Number(month.slice(5))}月
        </span>
        {isAdmin && (
          <select className="ui-input" aria-label="スタッフ" value={opId} onChange={(e) => setOpId(e.target.value)} style={{ minWidth: 200 }}>
            <option value="">代理で申請するスタッフ</option>
            {operators
              .filter((o) => o.status === 'active')
              .map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}({o.op_code})
                </option>
              ))}
          </select>
        )}
      </FilterBar>

      {/* ---- 申請する ---- */}
      <Panel
        title="シフトの変更を申請する"
        note="提出したシフトを変えたいときは、日・変更したい内容・**理由**を入れて申請します。管理者が承認すると、シフト提出に反映されます"
      >
        {!opId ? (
          <div className="ui-empty">{isAdmin ? '代理で申請するスタッフを選んでください' : 'アカウントにアポインターが紐づいていません。管理者に連絡してください。'}</div>
        ) : (
          <div className="report-toolbar" style={{ gap: 'var(--space-3)', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label className="ui-field-inline">
              <span className="ui-hint">日</span>
              <select className="ui-input" value={day} onChange={(e) => setDay(e.target.value)} style={{ width: 120 }}>
                <option value="">選ぶ</option>
                {days.map((d) => (
                  <option key={d} value={d}>
                    {Number(d)}日{weekdayLabel(`${month}-${d}` as DateStr)}
                  </option>
                ))}
              </select>
            </label>
            <span className="ui-field-inline">
              <span className="ui-hint">いまの提出</span>
              <b>{day ? wishLabel(current) : '—'}</b>
            </span>
            <label className="ui-field-inline">
              <span className="ui-hint">変更後</span>
              <select className="ui-input" aria-label="変更後の種類" value={rest ? 'rest' : 'work'} onChange={(e) => setRest(e.target.value === 'rest')} style={{ width: 100 }}>
                <option value="work">時間を変える</option>
                <option value="rest">休みにする</option>
              </select>
            </label>
            {!rest && (
              <>
                <label className="ui-field-inline">
                  <span className="ui-hint">開始</span>
                  <select className="ui-input" value={start} onChange={(e) => setStart(Number(e.target.value))} style={{ width: 96 }}>
                    {hours.map((h) => (
                      <option key={h} value={h}>
                        {hhmm(h)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="ui-field-inline">
                  <span className="ui-hint">終了</span>
                  <select className="ui-input" value={end} onChange={(e) => setEnd(Number(e.target.value))} style={{ width: 96 }}>
                    {hours.map((h) => (
                      <option key={h} value={h}>
                        {hhmm(h)}
                      </option>
                    ))}
                  </select>
                </label>
              </>
            )}
            <label className="ui-field-inline" style={{ flex: '1 1 260px' }}>
              <span className="ui-hint">理由</span>
              <input className="ui-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="例: 通院のため" style={{ width: '100%' }} />
            </label>
            <button type="button" className="ui-btn ui-btn-primary" disabled={busy || !day || !reason.trim()} onClick={() => void submit()}>
              {busy ? '送信中…' : '申請する'}
            </button>
          </div>
        )}
      </Panel>

      {/* ---- 申請の一覧 ---- */}
      <Panel
        title={isAdmin ? '変更申請' : '自分の変更申請'}
        meta={pending.length > 0 ? `承認待ち ${pending.length}件` : undefined}
        note={isAdmin ? '承認するとシフト提出のその日が書き換わり、シフト表の変更履歴に残ります' : undefined}
      >
        {loading ? (
          <div className="ui-empty">読み込み中…</div>
        ) : list.length === 0 ? (
          <div className="ui-empty">この月の申請はありません</div>
        ) : (
          <div className="report-scroll is-auto">
            <table className="report-table">
              <caption className="sr-only">シフト変更の申請</caption>
              <thead>
                <tr>
                  <th scope="col" style={{ width: 88 }}>状態</th>
                  {isAdmin && <th scope="col" style={{ width: 120 }}>スタッフ</th>}
                  <th scope="col" style={{ width: 84 }}>日</th>
                  <th scope="col" style={{ width: 200 }}>変更前 → 変更後</th>
                  <th scope="col">理由</th>
                  <th scope="col" style={{ width: 160 }}>結果</th>
                  <th scope="col" style={{ width: 150 }} />
                </tr>
              </thead>
              <tbody>
                {list.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <StatusBadge kind={kindOf(r.status)} label={CHANGE_STATUS_LABELS[r.status]} />
                    </td>
                    {isAdmin && <td>{r.name}</td>}
                    <td>
                      {Number(r.day)}日{weekdayLabel(`${r.month}-${r.day}` as DateStr)}
                    </td>
                    <td>
                      {wishLabel(r.before)} → <b>{wishLabel(r.after)}</b>
                    </td>
                    <td>{r.reason}</td>
                    <td className="ui-hint">{r.status === 'pending' ? '—' : `${r.decided_by_name ?? ''}${r.decision_note ? `: ${r.decision_note}` : ''}`}</td>
                    <td>
                      {r.status !== 'pending' ? null : isAdmin ? (
                        <span style={{ display: 'flex', gap: 'var(--space-2)' }}>
                          <button type="button" className="ui-btn ui-btn-primary ui-btn-sm" disabled={busy} onClick={() => void decide(r, true)}>
                            承認
                          </button>
                          <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" disabled={busy} onClick={() => void decide(r, false)}>
                            却下
                          </button>
                        </span>
                      ) : (
                        <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" disabled={busy} onClick={() => void cancel(r)}>
                          取り下げ
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </>
  );
}
